import React, { useState, useEffect } from 'react';
import { extractTeachingStandardId } from '../../../utils/mongodb';
import { GRADE_LEVELS_K12, type GradeLevelK12 } from '../../../constants/grades';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  Divider,
  Typography,
  Box,
  Alert,
  Chip,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import apiAxiosInstance from '../../../utils/axiosInstance';
import { hasReading, hasMath } from '../../../utils/subjectHelpers';
import { aiAxiosInstance } from '../../../utils/axiosInstance';
import { useTeacher } from '../../../context/TeacherContext';

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  gradeLevel?: string;
}

interface SessionPlannerDialogProps {
  open: boolean;
  onClose: () => void;
  onSessionCreated: (session: any) => void;
  preselectedScope?: 'student' | 'group' | null;
  preselectedId?: string | null;
  preselectedName?: string;
}

const GRADE_LEVELS = [...GRADE_LEVELS_K12];

// Subject options for dropdown - internal value vs display label
const SUBJECT_OPTIONS = [
  { value: 'ELA', label: 'Reading / ELA', check: hasReading },
  { value: 'Math', label: 'Mathematics', check: hasMath },
] as const;

const STEPS = ['Select Students', 'Define Focus', 'Plan Details'];

const SessionPlannerDialog: React.FC<SessionPlannerDialogProps> = ({
  open,
  onClose,
  onSessionCreated,
  preselectedScope,
  preselectedId,
  preselectedName,
}) => {
  const { teacher } = useTeacher();
  const [activeStep, setActiveStep] = useState(0);
  
  // Student selection
  const [students, setStudents] = useState<Student[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<'students' | 'group'>('students');
  
  // New student form
  const [newStudentFirstName, setNewStudentFirstName] = useState('');
  const [newStudentLastName, setNewStudentLastName] = useState('');
  const [newStudentGrade, setNewStudentGrade] = useState('');
  const [addingStudent, setAddingStudent] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Session details
  const [sessionTitle, setSessionTitle] = useState('');
  const [focusSkill, setFocusSkill] = useState('');
  const [focusStandard, setFocusStandard] = useState('');
  const [duration, setDuration] = useState(20);
  const [objectives, setObjectives] = useState<string[]>([]);
  const [newObjective, setNewObjective] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [newMaterial, setNewMaterial] = useState('');
  
  // Standards selection
  const [availableStandards, setAvailableStandards] = useState<any[]>([]);
  const [availableGradeLevels, setAvailableGradeLevels] = useState<string[]>(GRADE_LEVELS);
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [loadingStandards, setLoadingStandards] = useState(false);
  
  // AI Plan Generation
  const [generatedPlan, setGeneratedPlan] = useState<any>(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadStudentsAndGroups();
      // If preselected, set the selection
      if (preselectedScope === 'student' && preselectedId) {
        setSelectionMode('students');
        setSelectedStudentIds([preselectedId]);
      } else if (preselectedScope === 'group' && preselectedId) {
        setSelectionMode('group');
        setSelectedGroupId(preselectedId);
      }
      // Load grade levels when dialog opens
      fetchGradeLevels();
    }
  }, [open, preselectedScope, preselectedId]);

  // Initialize grade level from teacher profile
  useEffect(() => {
    if (teacher?.gradeLevel && !selectedGrade) {
      const grade = teacher.gradeLevel;
      if (GRADE_LEVELS.includes(grade as GradeLevelK12)) {
        setSelectedGrade(grade);
      }
    }
  }, [teacher?.gradeLevel, selectedGrade]);

  // Fetch available grade levels
  const fetchGradeLevels = async () => {
    try {
      const params: Record<string, string> = {};
      const teachingStandardsId = extractTeachingStandardId(teacher?.teachingStandards);
      if (teachingStandardsId) params.teachingStandards = teachingStandardsId;
      if (selectedSubject) params.subject = selectedSubject;

      const response = await apiAxiosInstance.get('/api/detailedstandards/grade-levels', { params });
      setAvailableGradeLevels(Array.isArray(response.data) ? response.data : GRADE_LEVELS);
    } catch (err) {
      console.error('Error fetching grade levels:', err);
      setAvailableGradeLevels(GRADE_LEVELS);
    }
  };

  // Fetch standards when grade or subject changes
  useEffect(() => {
    const fetchStandards = async () => {
      if (!selectedGrade) {
        setAvailableStandards([]);
        return;
      }

      setLoadingStandards(true);
      try {
        const params: Record<string, string> = { gradeLevel: selectedGrade };
        if (selectedSubject) params.subject = selectedSubject;
        
        const teachingStandardsId = extractTeachingStandardId(teacher?.teachingStandards);
        if (teachingStandardsId) params.teachingStandards = teachingStandardsId;

        const response = await apiAxiosInstance.get('/api/detailedstandards', { params });
        setAvailableStandards(response.data || []);
      } catch (err) {
        console.error('Error fetching standards:', err);
        setAvailableStandards([]);
      } finally {
        setLoadingStandards(false);
      }
    };

    fetchStandards();
  }, [selectedGrade, selectedSubject, teacher?.teachingStandards]);

  const loadStudentsAndGroups = async () => {
    try {
      const [s, g] = await Promise.all([
        apiAxiosInstance.get('/api/students'),
        apiAxiosInstance.get('/api/groups'),
      ]);
      setStudents(s.data || []);
      setGroups(g.data || []);
    } catch (err) {
      console.error('Failed to load students/groups', err);
    }
  };

  const handleClose = () => {
    // Reset all state
    setActiveStep(0);
    setSelectedStudentIds([]);
    setSelectedGroupId(null);
    setSelectionMode('students');
    setSessionTitle('');
    setFocusSkill('');
    setFocusStandard('');
    setDuration(20);
    setObjectives([]);
    setMaterials([]);
    setAvailableStandards([]);
    setSelectedGrade('');
    setSelectedSubject('');
    setGeneratedPlan(null);
    setError(null);
    setNewStudentFirstName('');
    setNewStudentLastName('');
    setNewStudentGrade('');
    setActiveTab(0);
    onClose();
  };

  const resetNewStudentForm = () => {
    setNewStudentFirstName('');
    setNewStudentLastName('');
    setNewStudentGrade('');
  };

  const toggleStudent = (studentId: string) => {
    const id = String(studentId);
    setSelectedStudentIds((prev) =>
      prev.includes(id)
        ? prev.filter((sid) => sid !== id)
        : [...prev, id]
    );
  };

  const handleAddNewStudent = async () => {
    if (!newStudentFirstName.trim() || !newStudentLastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!newStudentGrade) {
      setError('Grade level is required');
      return;
    }

    if (!teacher?._id) {
      setError('You must be logged in to add a student');
      return;
    }

    setAddingStudent(true);
    setError(null);

    try {
      const response = await apiAxiosInstance.post('/api/students', {
        teacherId: teacher._id,
        firstName: newStudentFirstName.trim(),
        lastName: newStudentLastName.trim(),
        gradeLevel: newStudentGrade,
      });

      const newStudent = response.data.student || response.data;
      
      const normalizedStudent: Student = {
        _id: String(newStudent._id),
        firstName: newStudent.firstName || '',
        lastName: newStudent.lastName || '',
        gradeLevel: newStudent.gradeLevel,
      };
      
      setStudents((prev) => [...prev, normalizedStudent]);
      setSelectedStudentIds((prev) => [...prev, normalizedStudent._id]);
      resetNewStudentForm();
      setActiveTab(0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add student');
    } finally {
      setAddingStudent(false);
    }
  };

  const generateAIPlan = async () => {
    if (!focusSkill.trim()) return;
    
    setGeneratingPlan(true);
    setError(null);
    
    try {
      const response = await fetch(`${aiAxiosInstance.defaults.baseURL}/instruction/plan/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          focusSkill: focusSkill.trim(),
          focusStandard: focusStandard.trim() || undefined,
          scope: selectionMode === 'group' ? 'group' : selectedStudentIds.length > 1 ? 'group' : 'student',
          duration,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate plan');
      }
      
      const plan = await response.json();
      setGeneratedPlan(plan);
      
      // Auto-fill from generated plan if fields are empty
      if (!sessionTitle && plan.title) setSessionTitle(plan.title);
      if (objectives.length === 0 && plan.objectives) setObjectives(plan.objectives);
      if (materials.length === 0 && plan.materials) setMaterials(plan.materials);
    } catch (err: any) {
      setError(err.message || 'Failed to generate plan');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleCreateSession = async () => {
    if (!focusSkill.trim() && !focusStandard.trim()) {
      setError('Please enter a focus skill or select a standard');
      return;
    }

    // Determine scope
    let scope: { type: 'student' | 'group'; studentId?: string; groupId?: string };
    
    if (selectionMode === 'group' && selectedGroupId) {
      scope = { type: 'group', groupId: selectedGroupId };
    } else if (selectedStudentIds.length === 1) {
      scope = { type: 'student', studentId: selectedStudentIds[0] };
    } else if (selectedStudentIds.length > 1) {
      // Create a temporary group for multiple students
      try {
        const groupRes = await apiAxiosInstance.post('/api/groups', {
          name: sessionTitle || `Session Group - ${focusSkill}`,
          type: 'instruction',
          students: selectedStudentIds,
        });
        scope = { type: 'group', groupId: groupRes.data._id };
      } catch (err: any) {
        setError('Failed to create group for session');
        return;
      }
    } else {
      setError('Please select at least one student');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        scope,
        title: sessionTitle.trim() || undefined,
        focusSkill: focusSkill.trim(),
        focusStandard: focusStandard.trim() || undefined,
        duration,
        objectives,
        materials,
        planNotes: generatedPlan ? JSON.stringify({
          openingActivity: generatedPlan.openingActivity,
          mainActivity: generatedPlan.mainActivity,
          closingActivity: generatedPlan.closingActivity,
          differentiation: generatedPlan.differentiation,
          suggestedScripts: generatedPlan.suggestedScripts,
        }) : undefined,
      };
      
      const response = await apiAxiosInstance.post('/api/instruction/sessions', payload);
      const created = response.data;

      onSessionCreated(created);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate selection
      if (selectionMode === 'students' && selectedStudentIds.length === 0) {
        setError('Please select at least one student');
        return;
      }
      if (selectionMode === 'group' && !selectedGroupId) {
        setError('Please select a group');
        return;
      }
    }
    
    if (activeStep === 1) {
      // Validate that at least focus skill or standard is provided
      if (!focusSkill.trim() && !focusStandard.trim()) {
        setError('Please enter a focus skill or select a standard');
        return;
      }
      // Auto-generate plan if not already generated
      if (!generatedPlan && !generatingPlan) {
        generateAIPlan();
      }
    }
    
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const addObjective = () => {
    if (newObjective.trim()) {
      setObjectives([...objectives, newObjective.trim()]);
      setNewObjective('');
    }
  };

  const removeObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  const addMaterial = () => {
    if (newMaterial.trim()) {
      setMaterials([...materials, newMaterial.trim()]);
      setNewMaterial('');
    }
  };

  const removeMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  // Render step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Tabs value={selectionMode} onChange={(_, v) => setSelectionMode(v)} sx={{ mb: 2 }}>
              <Tab label="Select Students" value="students" />
              <Tab label="Select Group" value="group" />
            </Tabs>
            
            {selectionMode === 'students' ? (
              <>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1 }}>
                  <Tab label="Select Existing" />
                  <Tab label="Add New Student" />
                </Tabs>
                
                {activeTab === 0 ? (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {students.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                        No students available. Click "Add New Student" tab to add one.
                      </Typography>
                    ) : (
                      <List dense>
                        {students.map((student) => (
                          <ListItem
                            key={student._id}
                            button
                            onClick={() => toggleStudent(student._id)}
                            selected={selectedStudentIds.includes(String(student._id))}
                          >
                            <Checkbox
                              checked={selectedStudentIds.includes(String(student._id))}
                              tabIndex={-1}
                              disableRipple
                            />
                            <ListItemAvatar>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {(student.firstName?.[0] || '') + (student.lastName?.[0] || '')}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={`${student.firstName || 'Unknown'} ${student.lastName || ''}`}
                              secondary={student.gradeLevel ? `Grade ${student.gradeLevel}` : 'No grade'}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                    
                    {selectedStudentIds.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {selectedStudentIds.length} student(s) selected
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ py: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name *"
                          value={newStudentFirstName}
                          onChange={(e) => setNewStudentFirstName(e.target.value)}
                          autoFocus
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name *"
                          value={newStudentLastName}
                          onChange={(e) => setNewStudentLastName(e.target.value)}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel>Grade Level *</InputLabel>
                          <Select
                            value={newStudentGrade}
                            label="Grade Level"
                            onChange={(e) => setNewStudentGrade(e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select Grade</em>
                            </MenuItem>
                            {GRADE_LEVELS.map((grade) => (
                              <MenuItem key={grade} value={grade}>
                                Grade {grade}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={handleAddNewStudent}
                          disabled={addingStudent}
                          startIcon={<PersonIcon />}
                          fullWidth
                        >
                          {addingStudent ? 'Adding...' : 'Add Student & Select'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {groups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    No groups available. Create a group first from the roster.
                  </Typography>
                ) : (
                  <List dense>
                    {groups.map((group) => (
                      <ListItem
                        key={group._id}
                        button
                        onClick={() => setSelectedGroupId(group._id)}
                        selected={selectedGroupId === group._id}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                            <GroupIcon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={group.name || 'Unnamed Group'}
                          secondary={`${group.students?.length || 0} students`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Box>
        );
      
      case 1:
        return (
          <Stack spacing={3}>
            <TextField
              label="Focus Skill"
              value={focusSkill}
              onChange={(e) => setFocusSkill(e.target.value)}
              placeholder="e.g., Identifying Main Idea"
              fullWidth
              helperText="What skill will students be working on? (optional if standard is selected)"
            />
            
            {/* Grade Level Selection */}
            <FormControl fullWidth>
              <InputLabel>Grade Level</InputLabel>
              <Select
                value={selectedGrade}
                label="Grade Level"
                onChange={(e) => {
                  setSelectedGrade(e.target.value);
                  setFocusStandard(''); // Reset standard when grade changes
                }}
              >
                <MenuItem value="">
                  <em>Select Grade Level</em>
                </MenuItem>
                {availableGradeLevels.map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    Grade {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Subject Selection - Only show subjects teacher has */}
            <FormControl fullWidth>
              <InputLabel>Subject (optional)</InputLabel>
              <Select
                value={selectedSubject}
                label="Subject (optional)"
                onChange={(e) => {
                  setSelectedSubject(e.target.value);
                  setFocusStandard(''); // Reset standard when subject changes
                }}
              >
                <MenuItem value="">
                  <em>All Subjects</em>
                </MenuItem>
                {SUBJECT_OPTIONS.filter((opt) => opt.check(teacher)).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Standards Dropdown */}
            <FormControl fullWidth disabled={!selectedGrade || loadingStandards}>
              <InputLabel>Focus Standard (optional)</InputLabel>
              <Select
                value={focusStandard}
                label="Focus Standard (optional)"
                onChange={(e) => setFocusStandard(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return '';
                  const std = availableStandards.find((s: any) => 
                    (s.code || s.standard || s._id) === selected
                  );
                  if (!std) return selected;
                  const code = std.code || std.standard || '';
                  const desc = std.description || '';
                  return desc ? `${code} - ${desc.length > 60 ? desc.substring(0, 60) + '...' : desc}` : code;
                }}
              >
                <MenuItem value="">
                  <em>Select a standard (optional)</em>
                </MenuItem>
                {availableStandards.map((standard: any) => {
                  const code = standard.code || standard.standard || standard.standardIdentifier || '';
                  const description = standard.description || standard.short_description || '';
                  return (
                    <MenuItem 
                      key={standard._id || code} 
                      value={code || standard._id}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                        {code}{description ? ` - ${description}` : ''}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
              {loadingStandards && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Loading standards...
                </Typography>
              )}
              {selectedGrade && availableStandards.length === 0 && !loadingStandards && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No standards found for this grade/subject combination
                </Typography>
              )}
            </FormControl>
            
            <TextField
              label="Duration (minutes)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 20)}
              fullWidth
              InputProps={{
                endAdornment: <Typography variant="caption" color="text.secondary">min</Typography>,
              }}
            />
            
            {generatedPlan && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LightbulbIcon color="primary" />
                  <Typography variant="subtitle2">AI-Generated Plan</Typography>
                </Box>
                <Typography variant="body2">
                  <strong>Opening:</strong> {generatedPlan.openingActivity}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Main Activity:</strong> {generatedPlan.mainActivity}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Closing:</strong> {generatedPlan.closingActivity}
                </Typography>
              </Paper>
            )}
            
            <Button
              variant="outlined"
              onClick={generateAIPlan}
              disabled={generatingPlan || !focusSkill.trim()}
              startIcon={<LightbulbIcon />}
            >
              {generatingPlan ? 'Generating...' : generatedPlan ? 'Regenerate Plan' : 'Generate AI Plan'}
            </Button>
          </Stack>
        );
      
      case 2:
        return (
          <Stack spacing={3}>
            <TextField
              label="Session Title"
              value={sessionTitle}
              onChange={(e) => setSessionTitle(e.target.value)}
              placeholder="e.g., Main Idea Practice with Sarah"
              fullWidth
              helperText="A descriptive title for this session (optional)"
            />
            
            {/* Objectives */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Learning Objectives</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  value={newObjective}
                  onChange={(e) => setNewObjective(e.target.value)}
                  placeholder="Add an objective..."
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addObjective();
                    }
                  }}
                />
                <Button variant="outlined" onClick={addObjective} size="small">
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {objectives.map((obj, idx) => (
                  <Chip
                    key={idx}
                    label={obj}
                    onDelete={() => removeObjective(idx)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
            
            {/* Materials */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Materials Needed</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  value={newMaterial}
                  onChange={(e) => setNewMaterial(e.target.value)}
                  placeholder="Add a material..."
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addMaterial();
                    }
                  }}
                />
                <Button variant="outlined" onClick={addMaterial} size="small">
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {materials.map((mat, idx) => (
                  <Chip
                    key={idx}
                    label={mat}
                    onDelete={() => removeMaterial(idx)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
            
            {/* AI Plan Preview */}
            {generatedPlan && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  <LightbulbIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  AI Suggested Activities
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Opening ({generatedPlan.openingActivity?.length > 50 ? generatedPlan.openingActivity.substring(0, 50) + '...' : generatedPlan.openingActivity})</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Main:</strong> {generatedPlan.mainActivity?.length > 100 ? generatedPlan.mainActivity.substring(0, 100) + '...' : generatedPlan.mainActivity}
                </Typography>
                {generatedPlan.differentiation && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Differentiation available
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Stack>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon />
          Plan New Instruction Session
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<ChevronLeftIcon />}>
            Back
          </Button>
        )}
        
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ChevronRightIcon />}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleCreateSession}
            disabled={loading || !focusSkill.trim()}
            startIcon={<AddIcon />}
          >
            {loading ? 'Creating...' : 'Create Session'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SessionPlannerDialog;
